import { Children, ReactNode } from "react";
import {
  VIEW_MODES,
  DEFAULT_OFFSET,
  MIN_SLIDE_INDEX,
  ACTIVE_SLIDE_ALIGNMENTS,
} from "./constants";
import { ActiveSlideAlignment, ViewModes } from "./types";

export const mapChildrenToElements = (children: ReactNode) =>
  Children.toArray(children).filter(Boolean);

export const getMaxSlideIndex = (slidesCount: number) =>
  slidesCount > 0 ? slidesCount - 1 : MIN_SLIDE_INDEX;

export const normalizeSlideIndex = ({
  slideIndex,
  slidesCount,
}: {
  slideIndex: number;
  slidesCount: number;
}) =>
  slideIndex < MIN_SLIDE_INDEX
    ? slidesCount - Math.abs(slideIndex)
    : slideIndex % slidesCount;

/**
 * Функция для проверки позиции ленты со слайдами относительно граничных значений.
 *
 * Пример: checkTrackLimitPositions({carouselWidth, trackWidth, trackOffset, activeSlideAlignment}) -> [true, false]
 */
export const getMultipleViewControlsVisibility = ({
  activeSlideWidth,
  carouselWidth,
  trackWidth,
  trackOffset,
  activeSlideAlignment,
}: {
  carouselWidth: number;
  trackWidth: number;
  trackOffset: number;
  activeSlideAlignment: ActiveSlideAlignment;
  activeSlideWidth: number;
}): [boolean, boolean] => {
  const isCenterAlignment =
    activeSlideAlignment === ACTIVE_SLIDE_ALIGNMENTS.CENTER;

  if (carouselWidth && trackWidth) {
    const additionalCenterOffset = activeSlideWidth / 2 + carouselWidth / 2;

    const isFirstSlideCentered =
      trackOffset < 0 &&
      Math.abs(additionalCenterOffset + trackOffset * -1 - carouselWidth) <= 1;

    const isLeftControlHidden = isCenterAlignment
      ? isFirstSlideCentered
      : trackOffset === 0;

    const isLastSlideCentered =
      Math.abs(additionalCenterOffset + trackOffset - trackWidth) <= 1;

    const isRightControlHidden = isCenterAlignment
      ? isLastSlideCentered
      : Math.round(trackWidth - trackOffset) <= Math.round(carouselWidth);

    return [!isLeftControlHidden, !isRightControlHidden];
  }

  return [false, false];
};

/**
 * Функция для проверки является ли активный слайд первым или последним.
 *
 * Пример: checkSlideLimitIndexes(0, 2) -> [true, false]
 * Пример: checkSlideLimitIndexes(1, 2) -> [false, false]
 * Пример: checkSlideLimitIndexes(2, 2) -> [false, true]
 * Пример: checkSlideLimitIndexes(0, 0) -> [true, true]
 */
export const getSingleSlideControlsVisibility = (
  activeSlideIndex: number,
  maxSlideIndex: number
): [boolean, boolean] => {
  if (maxSlideIndex) {
    if (activeSlideIndex === MIN_SLIDE_INDEX) {
      return [false, true];
    }

    if (activeSlideIndex === maxSlideIndex) {
      return [true, false];
    }

    if (
      activeSlideIndex > MIN_SLIDE_INDEX &&
      activeSlideIndex < maxSlideIndex
    ) {
      return [true, true];
    }
  }

  return [false, false];
};

export const getControlsVisibility = ({
  activeSlideIndex,
  maxSlideIndex,
  activeSlideAlignment,
  activeSlideWidth,
  carouselWidth,
  trackWidth,
  isMultipleViewMode,
  loopMode,
  trackOffset,
  shouldRenderExtraSlides,
}: {
  isMultipleViewMode: boolean;
  activeSlideIndex: number;
  maxSlideIndex: number;
  carouselWidth: number;
  trackWidth: number;
  trackOffset: number;
  loopMode: boolean | undefined;
  shouldRenderExtraSlides: boolean;
  activeSlideAlignment: ActiveSlideAlignment;
  activeSlideWidth: number;
}): [boolean, boolean] => {
  if (loopMode) {
    const showControl =
      maxSlideIndex > MIN_SLIDE_INDEX && shouldRenderExtraSlides;

    return [showControl, showControl];
  }

  return isMultipleViewMode
    ? getMultipleViewControlsVisibility({
        carouselWidth,
        trackWidth,
        trackOffset,
        activeSlideAlignment,
        activeSlideWidth,
      })
    : getSingleSlideControlsVisibility(activeSlideIndex, maxSlideIndex);
};

/**
 * Функция для определения значения смещения ленты со слайдами вдоль горизонтальной оси.
 */
export const getTrackOffset = ({
  activeSlideIndex,
  activeSlideAlignment,
  carouselElement,
  trackElement,
  gap,
  gapOffset,
  slides,
  viewMode,
  loopMode,
  slidesWidth,
  shouldRenderExtraSlides,
  extraSlidesMultiplier,
}: {
  carouselElement: HTMLElement | null;
  trackElement: HTMLElement | null;
  slides: HTMLElement[];
  activeSlideIndex: number;
  viewMode: ViewModes;
  gap: number;
  gapOffset: number;
  loopMode: boolean | undefined;
  slidesWidth: number[];
  shouldRenderExtraSlides: boolean;
  extraSlidesMultiplier: number;
  activeSlideAlignment: ActiveSlideAlignment;
}) => {
  const isMultiSlideMode = viewMode === VIEW_MODES.MULTIPLE;
  const isCenterAlignment =
    activeSlideAlignment === ACTIVE_SLIDE_ALIGNMENTS.CENTER;

  const normalizedSlideIndex = normalizeSlideIndex({
    slideIndex: activeSlideIndex,
    slidesCount: slides.length,
  });

  const activeSlideWidth = slidesWidth[normalizedSlideIndex];

  const carouselWidth = carouselElement?.getBoundingClientRect().width ?? 0;
  const additionalCenterOffset = carouselWidth / 2 - activeSlideWidth / 2;

  if (!loopMode) {
    const activeSlideElement = slides[activeSlideIndex];

    if (trackElement && carouselWidth > 0 && activeSlideIndex !== 0) {
      const { width: trackWidth } = trackElement.getBoundingClientRect();

      const rightTrackAlignmentOffset =
        trackElement.offsetWidth - carouselWidth;

      const activeSlideElementOffset =
        activeSlideElement.offsetLeft - gapOffset;

      // Если прокрутка приведёт к появлению пустого пространства между последним слайдом
      // и правой стороной карусели - выравниваем последний слайд по правой стороне карусели
      if (
        isMultiSlideMode &&
        activeSlideElementOffset + carouselWidth > trackWidth &&
        !isCenterAlignment
      ) {
        return Math.max(rightTrackAlignmentOffset, 0);
      }

      const notFirstSlideOffset = isCenterAlignment
        ? additionalCenterOffset - activeSlideElement.offsetLeft
        : activeSlideElementOffset;

      // Пролистываем ленту к конкретному слайду
      return isCenterAlignment ? notFirstSlideOffset * -1 : notFirstSlideOffset;
    }

    const firstSlideOffset = isCenterAlignment
      ? additionalCenterOffset * -1
      : DEFAULT_OFFSET;

    return firstSlideOffset;
  }

  let slidesToMeasure: number[] = [];
  const extraSlides = Array.from(
    {
      length: extraSlidesMultiplier,
    },
    () => slidesWidth
  ).flat();

  if (activeSlideIndex < MIN_SLIDE_INDEX) {
    slidesToMeasure = extraSlides.slice(0, activeSlideIndex);
  } else if (!shouldRenderExtraSlides) {
    slidesToMeasure = [];
  } else {
    slidesToMeasure = extraSlides.concat(
      slidesWidth.slice(0, activeSlideIndex)
    );
  }

  const loopModeSlidesGap = isMultiSlideMode ? gap : gapOffset;
  const previousSlidesWidth = slidesToMeasure.reduce(
    (totalWidth, slideWidth) => totalWidth + slideWidth + loopModeSlidesGap,
    0
  );

  const centerModeOffset =
    isCenterAlignment && isMultiSlideMode ? additionalCenterOffset : gapOffset;

  const resultOffset = previousSlidesWidth - centerModeOffset;

  return resultOffset;
};

export const isSlideIndexValid = (
  activeSlideIndex: number | undefined,
  {
    maxSlideIndex,
  }: {
    maxSlideIndex: number;
  }
): activeSlideIndex is number =>
  typeof activeSlideIndex === "number" &&
  !Number.isNaN(activeSlideIndex) &&
  activeSlideIndex >= MIN_SLIDE_INDEX &&
  activeSlideIndex <= maxSlideIndex;

export const getExtraSlidesMultiplier = ({
  slidesWidth,
  carouselWidth,
  gap,
}: {
  slidesWidth: number[];
  carouselWidth: number;
  gap: number;
}) => {
  const mainSlidesWidth = slidesWidth.reduce(
    (totalSlidesWidth, slideWidth) => totalSlidesWidth + slideWidth + gap,
    0
  );

  const getAllSlidesWidth = (extraSlidesMultiplier: number) => {
    const prevSlidesBlockCount = extraSlidesMultiplier;
    const afterSlidesBlockCount = extraSlidesMultiplier;

    const extraSlidesBlocksCount = prevSlidesBlockCount + afterSlidesBlockCount;

    const allSlidesWidth =
      mainSlidesWidth +
      extraSlidesBlocksCount * mainSlidesWidth +
      extraSlidesBlocksCount * gap;

    return allSlidesWidth;
  };

  let extraSlidesMultiplier = 1;
  let allSlidesWidth = getAllSlidesWidth(extraSlidesMultiplier);

  while (allSlidesWidth < carouselWidth) {
    extraSlidesMultiplier += 1;
    allSlidesWidth = getAllSlidesWidth(extraSlidesMultiplier);
  }

  return extraSlidesMultiplier + 1;
};
